import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api, getCurrentUser } from "@/lib/apiClient";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { MultiImageUpload } from "@/components/ui/image-upload";
import { AlertTriangle, Camera } from "lucide-react";

// Schema de validación
const incidentSchema = z.object({
  work_order_id: z.string().optional(),
  process_record_id: z.string().optional(),
  incident_type: z.enum(["defect", "machine_failure", "material_issue", "safety", "delay", "other"]),
  severity: z.enum(["low", "medium", "high", "critical"]),
  title: z.string().min(5, "El título debe tener al menos 5 caracteres"),
  description: z.string().min(10, "La descripción debe tener al menos 10 caracteres"),
  location: z.string().optional(),
  affected_quantity: z.number().min(0).optional(),
});

type IncidentFormData = z.infer<typeof incidentSchema>;

interface IncidentReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workOrderId?: string;
  processRecordId?: string;
}

export function IncidentReportDialog({
  open,
  onOpenChange,
  workOrderId,
  processRecordId,
}: IncidentReportDialogProps) {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [incidentImages, setIncidentImages] = useState<string[]>([]);

  // Cargar órdenes de trabajo si no se proporciona una
  const { data: workOrders } = useQuery({
    queryKey: ["work-orders-active"],
    queryFn: async () => {
      const { data, error } = await api
        .from("work_orders")
        .select("id, order_number, product_name")
        .eq("is_active", true)
        .in("status", ["pending", "in_progress"])
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !workOrderId,
  });

  const form = useForm<IncidentFormData>({
    resolver: zodResolver(incidentSchema),
    defaultValues: {
      work_order_id: workOrderId || "",
      process_record_id: processRecordId || "",
      incident_type: "defect",
      severity: "medium",
      title: "",
      description: "",
      location: "",
      affected_quantity: 0,
    },
  });

  const createIncident = useMutation({
    mutationFn: async (data: IncidentFormData) => {
      const user = getCurrentUser();
      if (!user) throw new Error("No autorizado");

      // Crear el registro de incidencia en audit_logs como evento de incidencia
      const incidentData = {
        user_id: user.id,
        action: "incident_report",
        table_name: "incidents",
        record_id: data.work_order_id || data.process_record_id || null,
        old_data: null,
        new_data: JSON.stringify({
          type: data.incident_type,
          severity: data.severity,
          title: data.title,
          description: data.description,
          location: data.location,
          affected_quantity: data.affected_quantity,
          work_order_id: data.work_order_id,
          process_record_id: data.process_record_id,
          images: incidentImages,
          reported_at: new Date().toISOString(),
        }),
      };

      const { error } = await api.from("audit_logs").insert([incidentData]);
      if (error) throw error;

      // Si hay imágenes, guardarlas en la galería
      if (incidentImages.length > 0) {
        for (const imageUrl of incidentImages) {
          await api.from("gallery").insert([{
            element_id: data.work_order_id || data.process_record_id || user.id,
            element_type: "incident",
            image_url: imageUrl,
            description: `Incidencia: ${data.title}`,
            uploaded_by: user.id,
          }]);
        }
      }

      // También crear un evento de trazabilidad si hay orden de trabajo
      if (data.work_order_id) {
        await api.from("traceability_events").insert([{
          work_order_id: data.work_order_id,
          event_type: "incident",
          description: `${getSeverityLabel(data.severity)}: ${data.title}`,
          created_by: user.id,
          metadata: JSON.stringify({
            incident_type: data.incident_type,
            severity: data.severity,
            images_count: incidentImages.length,
          }),
        }]);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
      queryClient.invalidateQueries({ queryKey: ["audit-logs"] });
      queryClient.invalidateQueries({ queryKey: ["traceability-events"] });
      toast.success("Incidencia reportada exitosamente");
      form.reset();
      setIncidentImages([]);
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error("Error al reportar la incidencia: " + error.message);
    },
  });

  const onSubmit = async (data: IncidentFormData) => {
    setIsSubmitting(true);
    try {
      await createIncident.mutateAsync(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getIncidentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      defect: "Defecto de Calidad",
      machine_failure: "Fallo de Máquina",
      material_issue: "Problema de Material",
      safety: "Incidente de Seguridad",
      delay: "Retraso de Producción",
      other: "Otro",
    };
    return labels[type] || type;
  };

  const getSeverityLabel = (severity: string) => {
    const labels: Record<string, string> = {
      low: "🟢 Baja",
      medium: "🟡 Media",
      high: "🟠 Alta",
      critical: "🔴 Crítica",
    };
    return labels[severity] || severity;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Reportar Incidencia
          </DialogTitle>
          <DialogDescription>
            Documenta cualquier problema o incidencia durante el proceso de producción.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            {/* Orden de Trabajo (si no viene predefinida) */}
            {!workOrderId && (
              <FormField
                control={form.control}
                name="work_order_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Orden de Trabajo</FormLabel>
                    <Select onValueChange={(v) => field.onChange(v === '__none__' ? '' : v)} value={field.value || '__none__'}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona una orden (opcional)" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">Sin orden específica</SelectItem>
                        {workOrders?.map((order) => (
                          <SelectItem key={order.id} value={order.id}>
                            {order.order_number} - {order.product_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="grid grid-cols-2 gap-4">
              {/* Tipo de Incidencia */}
              <FormField
                control={form.control}
                name="incident_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de Incidencia *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="defect">Defecto de Calidad</SelectItem>
                        <SelectItem value="machine_failure">Fallo de Máquina</SelectItem>
                        <SelectItem value="material_issue">Problema de Material</SelectItem>
                        <SelectItem value="safety">Incidente de Seguridad</SelectItem>
                        <SelectItem value="delay">Retraso de Producción</SelectItem>
                        <SelectItem value="other">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Severidad */}
              <FormField
                control={form.control}
                name="severity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Severidad *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="low">🟢 Baja</SelectItem>
                        <SelectItem value="medium">🟡 Media</SelectItem>
                        <SelectItem value="high">🟠 Alta</SelectItem>
                        <SelectItem value="critical">🔴 Crítica</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Título */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Ej: Grieta en pieza durante laminado" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Descripción */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripción *</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={4}
                      placeholder="Describe detalladamente qué ocurrió, cuándo, y cualquier información relevante..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              {/* Ubicación */}
              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ubicación</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: Estación de laminado 2" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Cantidad Afectada */}
              <FormField
                control={form.control}
                name="affected_quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Piezas Afectadas</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Sección de Imágenes */}
            <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
              <h3 className="font-semibold flex items-center gap-2">
                <Camera className="h-4 w-4" />
                Evidencia Fotográfica
              </h3>
              <p className="text-sm text-muted-foreground">
                Toma fotos del problema para documentar la incidencia. Es muy importante para el seguimiento.
              </p>
              <MultiImageUpload
                value={incidentImages}
                onChange={setIncidentImages}
                maxImages={5}
                maxSizeMB={3}
                label=""
              />
            </div>

            {/* Botones */}
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? "Reportando..." : "Reportar Incidencia"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  form.reset();
                  setIncidentImages([]);
                  onOpenChange(false);
                }}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

